/**
 * Nynch Assist must stay visible and clickable above record sheets/dialogs.
 * Record overlays use the shared Sheet/Dialog z-50 layer; Assist sits above
 * that and below toasts (z-[100]).
 *
 * The same dismiss wrapper also ignores sibling dialogs: a contact sheet and
 * a merge-review dialog are separate Radix roots, so focus on the later one
 * looks like an outside dismiss on the earlier one.
 */
import { preventForeignLayerDismiss } from '@/lib/overlayDismissGuard';

export const ASSIST_LAYER_ATTR = 'data-nynch-assist-layer';
export const ASSIST_PANEL_ATTR = 'data-nynch-assist-panel';

export const ASSIST_FAB_Z_CLASS = 'z-[60]';
export const ASSIST_BACKDROP_Z_CLASS = 'z-[60]';
export const ASSIST_PANEL_Z_CLASS = 'z-[70]';

type AssistDismissEvent = {
  preventDefault: () => void;
  target?: EventTarget | null;
  currentTarget?: EventTarget | null;
  detail?: { originalEvent?: Event };
};

export function isAssistLayerEvent(event: {
  target?: EventTarget | null;
  detail?: { originalEvent?: Event };
}): boolean {
  const originalTarget = event.detail?.originalEvent?.target;
  const target = originalTarget ?? event.target;
  if (!(target instanceof Element)) return false;
  return target.closest(`[${ASSIST_LAYER_ATTR}]`) !== null;
}

export function preventAssistLayerDismiss(event: AssistDismissEvent): boolean {
  if (!isAssistLayerEvent(event)) return false;
  event.preventDefault();
  return true;
}

export function withAssistLayerDismiss<E extends AssistDismissEvent>(
  existing?: (event: E) => void,
): (event: E) => void {
  return (event: E) => {
    if (preventAssistLayerDismiss(event)) return;
    if (preventForeignLayerDismiss(event)) return;
    existing?.(event);
  };
}
